import ArrowRightSlim from 'assets/Icons/Arrow/ArrowRightSlim';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import { useGetAiChatHistoryQueries } from 'api/Queries/CL/AiChat/useGetAiChatHistoryQueries';
import { useGetUserDetailQueries } from 'api/Queries/CL/User/useGetUserDetailQueries';
import { pagePaths } from 'constants/path';
import moment from 'moment';
import { CLAiChatHistoryContent, CLAiChatHistoryItem } from 'models/CL/CLApiTypes';
import { ICLUserInfo } from 'models/CL/CLBasicInterface';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { PageTitle } from 'styles/Global/commonStyles';
import { BackgroundColor, BorderColor, PrimaryColor, TextColor, WarningColor } from 'styles/Global/colorSemantic';
import { DiagnosisInnerLayout, DiagnosisLayout, HospitalPageLabel, TwoBreadCrumbContainer } from 'styles/Global/hospitalStyles';
import { Radius } from 'styles/Global/objectStyles';
import { Body2Bold, Body3Medium, Body3Regular, Body5Regular, Title1_Bold } from 'styles/Global/typography';
import { changeMobileForm } from 'utils/changeMobileForm';
import { calculateBirthdayAge } from 'utils/calculateAge';

const formatText = (value?: string | number | boolean | null) => {
    if (value === undefined || value === null || value === '') return '미입력';
    if (typeof value === 'boolean') return value ? '허용' : '비허용';
    return String(value);
};

const formatTimestamp = (value?: number) => {
    return value ? moment(value).format('YYYY/MM/DD HH:mm') : '미입력';
};

const formatBirthday = (birthday?: string) => {
    if (!birthday) return { date: '미입력', age: '' };
    const birthdayInfo = calculateBirthdayAge(birthday);
    return {
        date: birthdayInfo.formattedBirthday,
        age: birthdayInfo.ageString,
    };
};

const formatGender = (gender?: string) => {
    if (gender === 'male') return '남성';
    if (gender === 'female') return '여성';
    return '미입력';
};

const authTypeLabel = (authType?: number) => {
    switch (authType) {
        case 0:
            return 'Google';
        case 1:
            return 'Apple';
        case 2:
            return 'Kakao';
        default:
            return '미입력';
    }
};

const AI_CHAT_HISTORY_DAYS = 30;

const roleLabel = (role?: string) => {
    if (role === 'user') return '사용자';
    if (role === 'assistant') return 'AI';
    return role || '미입력';
};

const contentText = (content?: CLAiChatHistoryContent[]) => {
    if (!content || content.length === 0) return '내용 없음';

    return content
        .map((item) => {
            if (item.type === 'text') return item.text || '';
            if (item.type === 'image') return item.fileName ? `[이미지] ${item.fileName}` : '[이미지]';
            if (item.type === 'tool_call') return item.name ? `[도구 호출] ${item.name}` : '[도구 호출]';
            if (item.type === 'tool_result') return item.isError ? '[도구 결과 오류]' : '[도구 결과]';
            return `[${item.type}]`;
        })
        .filter(Boolean)
        .join('\n');
};

const truncateText = (text: string, maxLength = 180) => {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
};

const historyTimestampValue = (createdAt?: string) => {
    return createdAt ? moment(createdAt).valueOf() : 0;
};

const aiChatSummary = (history: CLAiChatHistoryItem[]) => {
    const totalTokens = history.reduce((sum, item) => sum + (item.useTokens || 0), 0);
    const userMessageCount = history.filter((item) => item.role === 'user').length;
    const assistantMessageCount = history.filter((item) => item.role === 'assistant').length;
    const reportedCount = history.filter((item) => item.isReported).length;
    const lastUsedAt = [...history]
        .sort((a, b) => historyTimestampValue(b.createdAt) - historyTimestampValue(a.createdAt))
        .find((item) => item.createdAt)?.createdAt;

    return {
        totalMessages: history.length,
        userMessageCount,
        assistantMessageCount,
        totalTokens,
        reportedCount,
        lastUsedAt,
    };
};

const CLUserDetailPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { userId } = useParams();
    const fallbackUserInfo = state as ICLUserInfo | null;
    const selectedUserId = userId || fallbackUserInfo?._id || '';
    const { data: userDetailData, isLoading } = useGetUserDetailQueries(selectedUserId, fallbackUserInfo?.hospitalId);
    const userInfo = userDetailData || fallbackUserInfo;
    const aiChatUserIds = useMemo(() => {
        return Array.from(new Set([selectedUserId, userInfo?.uid].filter((id): id is string => !!id)));
    }, [selectedUserId, userInfo?.uid]);
    const {
        data: aiChatHistoryData = [],
        isLoading: aiChatHistoryLoading,
        error: aiChatHistoryError,
    } = useGetAiChatHistoryQueries(aiChatUserIds, AI_CHAT_HISTORY_DAYS);
    const birthdayInfo = formatBirthday(userInfo?.birthday);
    const notificationAllowed = userInfo?.notificationAllowed;
    const aiSummary = aiChatSummary(aiChatHistoryData);
    const sortedAiChatHistory = [...aiChatHistoryData].sort((a, b) => historyTimestampValue(b.createdAt) - historyTimestampValue(a.createdAt));

    const basicRows = [
        { label: '이름', value: formatText(userInfo?.name) },
        { label: '이메일', value: formatText(userInfo?.email) },
        { label: '연락처', value: userInfo?.mobile ? changeMobileForm(userInfo.mobile) : '미입력' },
        { label: '성별', value: formatGender(userInfo?.gender) },
        { label: '생년월일', value: birthdayInfo.age ? `${birthdayInfo.date} (${birthdayInfo.age})` : birthdayInfo.date },
        { label: '칼로리랩 가입일', value: formatTimestamp(userInfo?.registrationTimestamp) },
        { label: '최근 수정일', value: formatTimestamp(userInfo?.updateTimestamp) },
        { label: '로그인 타입', value: authTypeLabel(userInfo?.authType) },
        { label: '병원 ID', value: formatText(userInfo?.hospitalId) },
        { label: '즐겨찾기', value: userInfo?.isFavorite ? '예' : '아니오' },
    ];

    const healthRows = [
        { label: '처방 섭취량', value: userInfo?.healthData?.targetCalories ? `${userInfo.healthData.targetCalories}kcal` : '미입력' },
        { label: '수분섭취 목표', value: userInfo?.healthData?.targetHydrate ? `${userInfo.healthData.targetHydrate}ml` : '미입력' },
        { label: '목표 걸음', value: userInfo?.healthData?.targetSteps ? `${userInfo.healthData.targetSteps}걸음` : '미입력' },
        { label: '체중', value: userInfo?.healthData?.weight ? `${userInfo.healthData.weight}kg` : '미입력' },
        { label: 'BMR', value: userInfo?.healthData?.bmr ? `${userInfo.healthData.bmr}kcal` : '미입력' },
        { label: '생활 패턴', value: formatText(userInfo?.healthData?.lifeStyle) },
        { label: '수분 단위', value: userInfo?.healthData?.hydrateUnit ? `${userInfo.healthData.hydrateUnit}ml` : '미입력' },
        { label: '기본 건강값', value: userInfo?.healthData?.isDefault ? '예' : '아니오' },
    ];

    const notificationRows = [
        { label: '식사 알림', value: formatText(notificationAllowed?.meal) },
        { label: '아침 알림', value: formatText(notificationAllowed?.meals?.breakfast) },
        { label: '점심 알림', value: formatText(notificationAllowed?.meals?.lunch) },
        { label: '저녁 알림', value: formatText(notificationAllowed?.meals?.dinner) },
        { label: '운동 알림', value: formatText(notificationAllowed?.exercise) },
        { label: '수분 알림', value: formatText(notificationAllowed?.water) },
        { label: '병원 메시지 알림', value: formatText(notificationAllowed?.clinic) },
    ];

    const planRows = [
        { label: '채팅 제한 횟수', value: formatText(userInfo?.planInfo?.chatLimitCount) },
        { label: '구독 상태', value: formatText(userInfo?.planInfo?.subscriptionStatus) },
        { label: 'UID', value: formatText(userInfo?.uid) },
        { label: '사용자 ID', value: formatText(userInfo?._id) },
    ];

    return (
        <DiagnosisLayout>
            {!userInfo || isLoading ? null : (
                <DiagnosisInnerLayout>
                    <TwoBreadCrumbContainer>
                        <HospitalPageLabel onClick={() => navigate(pagePaths.CLAdminUserLookup)}>사용자 조회</HospitalPageLabel>
                        <IconWrapper>
                            <ArrowRightSlim />
                        </IconWrapper>
                        <HospitalPageLabel onClick={() => window.location.reload()}>사용자 상세정보</HospitalPageLabel>
                    </TwoBreadCrumbContainer>
                    <PageTitle $marginbottom='20px'>사용자 상세정보</PageTitle>
                    <UserSummary>
                        <UserNameBlock>
                            <NameLine>
                                {userInfo.name || '미입력'}
                                {userInfo.gender === 'male' && <GenderMan $width='24px' $height='24px' />}
                                {userInfo.gender === 'female' && <GenderWoman $width='24px' $height='24px' />}
                            </NameLine>
                            <SubInfo>{userInfo.email || '미입력'}</SubInfo>
                        </UserNameBlock>
                        <SummaryItem>
                            <Body5Regular>연락처</Body5Regular>
                            <Body3Medium>{userInfo.mobile ? changeMobileForm(userInfo.mobile) : '미입력'}</Body3Medium>
                        </SummaryItem>
                        <SummaryItem>
                            <Body5Regular>가입일</Body5Regular>
                            <Body3Medium>{formatTimestamp(userInfo.registrationTimestamp)}</Body3Medium>
                        </SummaryItem>
                    </UserSummary>
                    <DetailGrid>
                        <DetailSection title='기본 정보' rows={basicRows} />
                        <DetailSection title='건강 정보' rows={healthRows} />
                        <DetailSection title='알림 설정' rows={notificationRows} />
                        <DetailSection title='플랜 및 식별 정보' rows={planRows} />
                    </DetailGrid>
                    <AiChatSection>
                        <SectionTitle>AI 채팅 사용 현황</SectionTitle>
                        <AiSummaryGrid>
                            <AiSummaryItem>
                                <Body5Regular>조회 기간</Body5Regular>
                                <Body3Medium>최근 {AI_CHAT_HISTORY_DAYS}일</Body3Medium>
                            </AiSummaryItem>
                            <AiSummaryItem>
                                <Body5Regular>전체 메시지</Body5Regular>
                                <Body3Medium>{aiSummary.totalMessages}건</Body3Medium>
                            </AiSummaryItem>
                            <AiSummaryItem>
                                <Body5Regular>사용자 / AI</Body5Regular>
                                <Body3Medium>{aiSummary.userMessageCount}건 / {aiSummary.assistantMessageCount}건</Body3Medium>
                            </AiSummaryItem>
                            <AiSummaryItem>
                                <Body5Regular>사용 토큰</Body5Regular>
                                <Body3Medium>{aiSummary.totalTokens.toLocaleString()} tokens</Body3Medium>
                            </AiSummaryItem>
                            <AiSummaryItem>
                                <Body5Regular>신고 기록</Body5Regular>
                                <Body3Medium>{aiSummary.reportedCount}건</Body3Medium>
                            </AiSummaryItem>
                            <AiSummaryItem>
                                <Body5Regular>마지막 사용일</Body5Regular>
                                <Body3Medium>{aiSummary.lastUsedAt ? moment(aiSummary.lastUsedAt).format('YYYY/MM/DD HH:mm') : '미입력'}</Body3Medium>
                            </AiSummaryItem>
                        </AiSummaryGrid>
                        {aiChatHistoryLoading ? (
                            <AiEmptyText>AI 채팅 데이터를 불러오는 중입니다.</AiEmptyText>
                        ) : aiChatHistoryError ? (
                            <AiErrorText>{aiChatHistoryError instanceof Error ? aiChatHistoryError.message : 'AI 채팅 데이터를 불러오지 못했습니다.'}</AiErrorText>
                        ) : sortedAiChatHistory.length === 0 ? (
                            <AiEmptyText>최근 {AI_CHAT_HISTORY_DAYS}일 동안 AI 채팅 사용 기록이 없습니다.</AiEmptyText>
                        ) : (
                            <AiHistoryList>
                                {sortedAiChatHistory.slice(0, 20).map((item, idx) => (
                                    <AiHistoryItem key={item._id || `${item.createdAt}-${idx}`}>
                                        <AiHistoryMeta>
                                            <RoleBadge $role={item.role}>{roleLabel(item.role)}</RoleBadge>
                                            <Body5Regular>{item.createdAt ? moment(item.createdAt).format('YYYY/MM/DD HH:mm') : '미입력'}</Body5Regular>
                                            {item.useTokens !== undefined && <Body5Regular>{item.useTokens.toLocaleString()} tokens</Body5Regular>}
                                            {item.metadata?.model && <Body5Regular>{item.metadata.model}</Body5Regular>}
                                            {item.source === 'legacy' && <Body5Regular>기존</Body5Regular>}
                                            {item.isReported && <ReportedText>신고됨</ReportedText>}
                                        </AiHistoryMeta>
                                        <AiContent>{truncateText(contentText(item.content))}</AiContent>
                                        {item.reportReason && <ReportReason>신고 사유: {item.reportReason}</ReportReason>}
                                    </AiHistoryItem>
                                ))}
                            </AiHistoryList>
                        )}
                    </AiChatSection>
                </DiagnosisInnerLayout>
            )}
        </DiagnosisLayout>
    );
};

interface DetailSectionProps {
    title: string;
    rows: {
        label: string;
        value: string;
    }[];
}

const DetailSection = ({ title, rows }: DetailSectionProps) => {
    return (
        <Section>
            <SectionTitle>{title}</SectionTitle>
            <RowList>
                {rows.map((row) => (
                    <InfoRow key={row.label}>
                        <Label>{row.label}</Label>
                        <Value>{row.value}</Value>
                    </InfoRow>
                ))}
            </RowList>
        </Section>
    );
};

export default CLUserDetailPage;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
`;

const UserSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    width: 100%;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    background-color: ${BackgroundColor.color_background_white};
`;

const UserNameBlock = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 280px;
`;

const NameLine = styled(Title1_Bold)`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SubInfo = styled(Body3Regular)`
    color: ${TextColor.color_text_light};
`;

const SummaryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    width: 100%;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    background-color: ${BackgroundColor.color_background_white};
`;

const SectionTitle = styled(Body2Bold)`
    padding: 18px 20px;
    border-bottom: 1px solid ${BorderColor.color_border_neutral};
`;

const RowList = styled.div`
    display: flex;
    flex-direction: column;
`;

const InfoRow = styled.div`
    display: grid;
    grid-template-columns: 148px minmax(0, 1fr);
    min-height: 48px;
    border-bottom: 1px solid ${BorderColor.color_border_neutral};

    &:last-child {
        border-bottom: none;
    }
`;

const Label = styled(Body3Regular)`
    display: flex;
    align-items: center;
    padding: 0 18px;
    color: ${TextColor.color_text_light};
    background-color: ${BackgroundColor.color_background_neutral};
`;

const Value = styled(Body3Medium)`
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 0 18px;
    color: ${TextColor.color_text_neutral};
    word-break: break-word;
`;

const AiChatSection = styled(Section)`
    margin-top: 20px;
`;

const AiSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1px;
    padding: 20px;
    background-color: ${BorderColor.color_border_neutral};
`;

const AiSummaryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    padding: 16px;
    background-color: ${BackgroundColor.color_background_white};
`;

const AiHistoryList = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 20px 20px;
`;

const AiHistoryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 0;
    border-bottom: 1px solid ${BorderColor.color_border_neutral};

    &:last-child {
        border-bottom: none;
    }
`;

const AiHistoryMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: ${TextColor.color_text_light};
`;

const RoleBadge = styled(Body5Regular)<{ $role?: string }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    height: 28px;
    padding: 0 10px;
    border-radius: ${Radius.radius_4};
    color: ${({ $role }) => ($role === 'assistant' ? PrimaryColor.color_primary_normal : TextColor.color_text_neutral)};
    background-color: ${({ $role }) => ($role === 'assistant' ? BackgroundColor.color_background_primary_neutral : BackgroundColor.color_background_neutral)};
`;

const AiContent = styled(Body3Regular)`
    white-space: pre-wrap;
    word-break: break-word;
    color: ${TextColor.color_text_neutral};
`;

const AiEmptyText = styled(Body3Regular)`
    padding: 28px 20px;
    color: ${TextColor.color_text_light};
`;

const AiErrorText = styled(AiEmptyText)`
    color: ${WarningColor.color_warning_normal};
`;

const ReportedText = styled(Body5Regular)`
    color: ${WarningColor.color_warning_normal};
`;

const ReportReason = styled(Body5Regular)`
    color: ${TextColor.color_text_light};
    word-break: break-word;
`;
